<template>
  <control-select
    :value="value"
    multiple
    trackBy="id"
    label="caption"
    :placeholder="placeholder || filter.caption"
    :searchable="true"
    :internal-search="false"
    :options="combinedOptions"
    :loading="loading"
    @open="ensureInitialLoaded"
    @search-change="search"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-slot:afterList>
      <li>
        <div
          v-if="hasMore && !loading"
          class="autocomplete-multiselect__load-more"
          v-observe-visibility="loadMore"
        />
      </li>
    </template>
  </control-select>
</template>

<script>

export default {
  name: 'filter-autocomplete-multiselect-widget-component',

  props: [
    'value',
    'filter',
    'receive',
    'receiveSearch',
    'placeholder',
    // получаем полный result, а не только items
    'result',
    'loading',
    // актуальные значения остальных фильтров каталога
    'filterValues',
  ],

  data() {
    return {
      // локально накапливаем страницы autocomplete
      remoteOptions: [],
      pagination: {
        total: 0,
        limit: 20,
        offset: 0,
      },
      currentSearch: '',
      appendNextResult: false,
    };
  },

  computed: {
    combinedOptions() {
      const value = this.value || [];
      const options = this.remoteOptions || [];
      const optionKeys = options.map(({ id }) => id);

      // Сохраняем выбранные значения даже если их нет в текущей странице.
      return value
        .filter(({ id }) => !optionKeys.includes(id))
        .concat(options);
    },

    // есть ли следующая страница
    hasMore() {
      const {
        total = 0,
        limit = 20,
        offset = 0,
      } = this.pagination || {};

      return total > offset + limit;
    },

    // Контекст autocomplete — все текущие фильтры, кроме самого autocomplete.
    contextFilters() {
      const filters = this.normalizeFilters(
        this.filterValues || {}
      );

      if (
        this.filter
        && this.filter.id
      ) {
        delete filters[this.filter.id];
      }

      return filters;
    },

    // Используем ключ, чтобы инвалидировать уже загруженные страницы,
    // когда меняется любой внешний dependent-фильтр.
    contextKey() {
      return JSON.stringify(
        Object.keys(this.contextFilters)
          .sort()
          .reduce((result, key) => {
            result[key] = this.contextFilters[key];
            return result;
          }, {})
      );
    },
  },

  watch: {
    // ResourceLoaderController каждый новый response кладёт в result.
    // Для offset=0 заменяем список, для следующей страницы дописываем.
    result: {
      immediate: true,
      handler(result) {
        if (
          !result
          || !Array.isArray(result.items)
        ) {
          return;
        }

        // Запоминаем тип запроса до сброса appendNextResult.
        const append = this.appendNextResult;

        if (append) {
          const existingIds = this.remoteOptions.map(({ id }) => id);

          this.remoteOptions = this.remoteOptions.concat(
            result.items.filter(({ id }) => !existingIds.includes(id))
          );
        } else {
          this.remoteOptions = result.items;
        }

        this.pagination = result.pagination || {
          total: result.items.length,
          limit: 20,
          offset: 0,
        };

        this.appendNextResult = false;

        // Новая выдача должна открываться с начала.
        // При pagination позицию, наоборот, сохраняем.
        if (!append) {
          this.$nextTick(() => {
            this.resetDropdownScroll();
          });
        }
      },
    },

    // Загруженные страницы относятся к старому контексту и больше невалидны.
    contextKey(newValue, oldValue) {
      if (
        typeof oldValue === 'undefined'
        || newValue === oldValue
      ) {
        return;
      }

      this.invalidateRemoteOptions();
    },
  },

  methods: {
    normalizeFilterValue(value) {
      if (value instanceof Date) {
        return value.toISOString();
      }

      if (Array.isArray(value)) {
        return value
          .map(item => this.normalizeFilterValue(item))
          .filter(item => !this.isEmptyFilterValue(item));
      }

      if (value && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'id')) {
          return value.id;
        }

        return Object.keys(value).reduce((result, key) => {
          const normalized = this.normalizeFilterValue(value[key]);

          if (!this.isEmptyFilterValue(normalized)) {
            result[key] = normalized;
          }

          return result;
        }, {});
      }

      return value;
    },

    isEmptyFilterValue(value) {
      if (
        value === undefined
        || value === null
        || value === ''
      ) {
        return true;
      }

      if (Array.isArray(value)) {
        return value.length === 0;
      }

      if (
        typeof value === 'object'
        && !(value instanceof Date)
      ) {
        return Object.keys(value).length === 0;
      }

      return false;
    },

    normalizeFilters(filters = {}) {
      return Object.keys(filters).reduce((result, key) => {
        const value = this.normalizeFilterValue(filters[key]);

        if (!this.isEmptyFilterValue(value)) {
          result[key] = value;
        }

        return result;
      }, {});
    },

    resetPagination() {
      this.pagination = {
        total: 0,
        limit: 20,
        offset: 0,
      };
    },

    invalidateRemoteOptions() {
      this.remoteOptions = [];
      this.resetPagination();
      this.currentSearch = '';
      this.appendNextResult = false;
    },

    // общий метод запроса страницы autocomplete
    receivePage({
      search = '',
      offset = 0,
      append = false,
      receiver = this.receive,
    } = {}) {
      const filters = this.contextFilters;

      this.appendNextResult = append;

      receiver({
        search,
        filters: JSON.stringify(filters),
        limit: 20,
        offset,
      });
    },

    // первая contextual-страница
    loadInitial() {
      this.currentSearch = '';

      this.pagination = {
        total: 0,
        limit: 20,
        offset: 0,
      };

      this.appendNextResult = false;

      this.receivePage({
        search: '',
        offset: 0,
        append: false,
      });
    },

    // новый поиск всегда начинается с первой страницы
    search(q) {
      const search = (q || '').trim();

      this.currentSearch = search;

      if (search.length > 0 && search.length < 3) {
        return;
      }

      this.resetPagination();
      this.appendNextResult = false;

      // Старый список сейчас ещё находится в DOM,
      // поэтому сразу возвращаем scroll к началу.
      this.resetDropdownScroll();

      this.receivePage({
        search,
        offset: 0,
        append: false,
        receiver: this.receiveSearch,
      });
    },

    // Первую страницу загружаем лениво только один раз.
    // При повторном открытии уже загруженный список сохраняем.
    ensureInitialLoaded() {
      if (
        this.remoteOptions.length
        || this.loading
      ) {
        return;
      }

      this.loadInitial();
    },

    // догружаем следующую страницу при достижении конца списка
    loadMore(isVisible) {
      if (
        !isVisible
        || this.loading
        || !this.hasMore
      ) {
        return;
      }

      const nextOffset = (
        this.pagination.offset
        + this.pagination.limit
      );

      this.receivePage({
        search: this.currentSearch,
        offset: nextOffset,
        append: true,
      });
    },
    // возвращаем dropdown к началу при смене поискового контекста
    resetDropdownScroll() {
      if (!this.$el) {
        return;
      }

      const dropdown = this.$el.querySelector(
        '.multiselect__content-wrapper'
      );

      if (dropdown) {
        dropdown.scrollTop = 0;
      }
    },
  },
};

</script>

<style scoped>
.autocomplete-multiselect__load-more {
  min-height: 1px;
}
</style>